// Быстрое редактирование заданий текстом.
//
// Заполнять по одному пропуску в карточке (подпись, тип поля, баллы, список
// ответов) — это десятки полей на вопрос: в q2 их 20, в q3 — 13. Поэтому
// основной режим редактирования — обычный текст, строка = один пропуск:
//
//   James lives ___ Cambridge. = in
//   English coffee isn't very nice = English coffee is not very nice | is not
//
// Слева от первого «=» — текст задания (___ отмечает пропуск), справа —
// принимаемые ответы через «|». Для вопросов с выбором варианта справа стоят
// варианты, а правильный помечается звёздочкой:
//
//   I ___ Anna ten years ago. = *met | have met
//
// Звёздочек может быть несколько — это вопрос с несколькими правильными
// ответами, студент выбирает их все:
//
//   Which are irregular verbs? = *go | *write | want | listen
//
// Разделители выбраны так, чтобы не конфликтовать с содержимым: в текущем
// банке заданий ни «=», ни «|» внутри текстов не встречаются.

export const GAP_PLACEHOLDER = '___';
const ANSWER_SEPARATOR = '|';
const FIELD_SEPARATOR = '=';
const CORRECT_MARK = '*';

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

export const getLocalizedText = (value) => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value.en ?? value.ua ?? '';
  return '';
};

const getUa = (value) => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value.ua ?? value.en ?? '';
  return '';
};

// Подпись пропуска — это текст задания, он одинаков для обеих локалей.
// Исключение уже есть в банке (например «— пропуск 1 з 2» / «— blank 1 of 2»),
// поэтому украинский вариант не затираем, пока английский не изменили.
const buildLabel = (text, previousLabel) => {
  const previousEn = getLocalizedText(previousLabel);
  const previousUa = getUa(previousLabel);

  if (previousEn === text && previousUa && previousUa !== previousEn) {
    return { ua: previousUa, en: text };
  }

  return { ua: text, en: text };
};

const splitLine = (line) => {
  const separatorIndex = line.indexOf(FIELD_SEPARATOR);

  if (separatorIndex === -1) {
    return { text: line.trim(), answersPart: '' };
  }

  return {
    text: line.slice(0, separatorIndex).trim(),
    answersPart: line.slice(separatorIndex + 1).trim(),
  };
};

const splitAnswers = (answersPart) =>
  answersPart
    .split(ANSWER_SEPARATOR)
    .map((answer) => answer.trim())
    .filter(Boolean);

const buildGroupId = (questionId, index, usedIds) => {
  const base = questionId || 'q';
  let candidate = `${base}-g${index + 1}`;
  let suffix = index + 1;

  while (usedIds.has(candidate)) {
    suffix += 1;
    candidate = `${base}-g${suffix}`;
  }

  usedIds.add(candidate);

  return candidate;
};

// ——— Пропуски (text_input_group) ———

export function serializeGapLines(groups = []) {
  return groups
    .map((group) => {
      const text = getLocalizedText(group.label);
      const answers = (group.acceptedAnswers || []).join(` ${ANSWER_SEPARATOR} `);

      return answers ? `${text} ${FIELD_SEPARATOR} ${answers}` : text;
    })
    .join('\n');
}

export function parseGapLines(text, { existingGroups = [], questionId = 'q', defaults = {} } = {}) {
  const lines = String(text ?? '').split('\n');
  const usedIds = new Set();
  const groups = [];
  const issues = [];

  lines.forEach((rawLine) => {
    if (!rawLine.trim()) {
      return;
    }

    const index = groups.length;
    const previous = existingGroups[index] || null;
    const { text: label, answersPart } = splitLine(rawLine);
    const acceptedAnswers = splitAnswers(answersPart);

    if (!label) {
      issues.push({ line: index + 1, message: 'Немає тексту завдання' });
    }

    if (acceptedAnswers.length === 0) {
      issues.push({
        line: index + 1,
        message: `Немає правильної відповіді (після «${FIELD_SEPARATOR}»)`,
      });
    }

    const group = {
      id: previous?.id || buildGroupId(questionId, index, usedIds),
      label: buildLabel(label, previous?.label),
      inputKind: previous?.inputKind || defaults.inputKind || 'short',
      weight:
        typeof previous?.weight === 'number'
          ? previous.weight
          : Number(defaults.weight) || 1,
      acceptedAnswers,
    };

    if (Array.isArray(previous?.choices) && previous.choices.length > 0) {
      group.choices = [...previous.choices];
    }

    if (previous?.id) {
      usedIds.add(previous.id);
    }

    groups.push(group);
  });

  return { groups, issues };
}

// ——— Вибір варіанту (single_choice_group) ———

export function serializeChoiceLines(groups = []) {
  return groups
    .map((group) => {
      const text = getLocalizedText(group.label);
      const options = (group.options || [])
        .map((option) => {
          const optionText = getLocalizedText(option.text);
          return option.isCorrect ? `${CORRECT_MARK}${optionText}` : optionText;
        })
        .join(` ${ANSWER_SEPARATOR} `);

      return options ? `${text} ${FIELD_SEPARATOR} ${options}` : text;
    })
    .join('\n');
}

export function parseChoiceLines(text, { existingGroups = [], questionId = 'q' } = {}) {
  const lines = String(text ?? '').split('\n');
  const usedIds = new Set();
  const groups = [];
  const issues = [];

  lines.forEach((rawLine) => {
    if (!rawLine.trim()) {
      return;
    }

    const index = groups.length;
    const previous = existingGroups[index] || null;
    const { text: label, answersPart } = splitLine(rawLine);
    const rawOptions = splitAnswers(answersPart);

    if (!label) {
      issues.push({ line: index + 1, message: 'Немає тексту завдання' });
    }

    if (rawOptions.length < 2) {
      issues.push({
        line: index + 1,
        message: `Потрібно щонайменше два варіанти через «${ANSWER_SEPARATOR}»`,
      });
    }

    // Звёздочек может быть несколько: это вопрос с несколькими правильными
    // ответами. Проблема только одна — когда не отмечено ни одного.
    if (!rawOptions.some((option) => option.startsWith(CORRECT_MARK))) {
      issues.push({
        line: index + 1,
        message: `Жоден варіант не позначено «${CORRECT_MARK}» як правильний`,
      });
    }

    const groupId = previous?.id || buildGroupId(questionId, index, usedIds);

    if (previous?.id) {
      usedIds.add(previous.id);
    }

    groups.push({
      id: groupId,
      label: buildLabel(label, previous?.label),
      weight:
        typeof previous?.weight === 'number' ? previous.weight : 1,
      options: rawOptions.map((rawOption, optionIndex) => {
        const isCorrect = rawOption.startsWith(CORRECT_MARK);
        const optionText = isCorrect ? rawOption.slice(1).trim() : rawOption;
        const previousOption = previous?.options?.[optionIndex] || null;

        return {
          id: previousOption?.id || `${groupId}-o${optionIndex + 1}`,
          label: previousOption?.label || OPTION_LABELS[optionIndex] || String(optionIndex + 1),
          text: { ua: optionText, en: optionText },
          isCorrect,
        };
      }),
    });
  });

  return { groups, issues };
}

// ——— Общее ———

export const isTextQuestion = (question) => question?.type === 'text_input_group';

export function serializeQuestionLines(question) {
  return isTextQuestion(question)
    ? serializeGapLines(question.groups)
    : serializeChoiceLines(question.groups);
}

export function parseQuestionLines(question, text, defaults) {
  return isTextQuestion(question)
    ? parseGapLines(text, {
        existingGroups: question.groups,
        questionId: question.id,
        defaults,
      })
    : parseChoiceLines(text, {
        existingGroups: question.groups,
        questionId: question.id,
      });
}

// Рамка слів: у всех пропусков вопроса она одинаковая (это подсказка ко всему
// заданию), поэтому в форме редактируется одним полем.
export function getQuestionChoices(question) {
  const withChoices = (question?.groups || []).find(
    (group) => Array.isArray(group.choices) && group.choices.length > 0
  );

  return withChoices ? [...withChoices.choices] : [];
}

export function applyQuestionChoices(groups = [], choices = []) {
  return groups.map((group) => {
    const next = { ...group };

    if (choices.length > 0) {
      next.choices = [...choices];
    } else {
      delete next.choices;
    }

    return next;
  });
}

export function parseChoicesInput(value) {
  return String(value ?? '')
    .split(',')
    .map((choice) => choice.trim())
    .filter(Boolean);
}

export function summarizeQuestion(question) {
  const groups = question?.groups || [];
  const score = groups.reduce(
    (total, group) => total + (typeof group.weight === 'number' ? group.weight : 1),
    0
  );
  const incomplete = groups.filter((group) =>
    isTextQuestion(question)
      ? (group.acceptedAnswers || []).length === 0
      : !(group.options || []).some((option) => option.isCorrect)
  ).length;

  return { count: groups.length, score, incomplete };
}
